import { getChainConfigEntry } from '@/config/chain'
import { queryClient } from '@/config/query-client'
import { formFormat as formFormatCommon } from '@/domain/common/format'
import { TokenWithBalance, TokenWithBalanceAndFormValue, TokenWithValue } from '@/domain/common/types'
import { useAccount } from '@/domain/hooks/useAccount'
import { useIsHypercoreUser } from '@/domain/hooks/useIsHypercoreUser'
import { useIsUsingBigBlocks } from '@/domain/hooks/useIsUsingBigBlocks'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { MarketInfo, Reserve } from '@/domain/market-info/marketInfo'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { BaseUnitNumber, NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { l1BalancesQueryKey } from '@/domain/wallet/hypercore/query'
import { HyperCoreAsset } from '@/domain/wallet/hypercore/querySpotMeta'
import { useHyperCoreSpotMeta } from '@/domain/wallet/hypercore/useHyperCoreSpotMeta'
import { useL1Balances } from '@/domain/wallet/hypercore/useL1Balances'
import { marketBalancesQueryKey } from '@/domain/wallet/marketBalances'
import { MarketWalletInfo, useMarketWalletInfoForMarketQuery } from '@/domain/wallet/useMarketWalletInfo'
import { TokensInfo } from '@/domain/wallet/useTokens/TokenInfo'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { GasLimitExceededDetails } from '@/features/swap/logic/constants'
import { useGetPrice } from '@/features/swap/logic/priceQuery'
import { GetPriceError, GetPriceResult } from '@/features/swap/logic/types'
import { useSwapEvmTokens } from '@/features/swap/logic/useSwapEvmTokens'
import { useTokenBalance } from '@/features/swap/logic/useTokenBalance'
import { assert, raise } from '@/utils/assert'
import { useDebounce } from '@/utils/useDebounce'
import { zodResolver } from '@hookform/resolvers/zod'
import { SpotBalance } from '@nktkas/hyperliquid'
import { SpotToken } from '@nktkas/hyperliquid'
import * as hl from '@nktkas/hyperliquid'
import { usePrivy } from '@privy-io/react-auth'
import { useQuery } from '@tanstack/react-query'
import { BigNumber } from 'bignumber.js'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { Address, zeroAddress } from 'viem'
import { z } from 'zod'
import { getTradeType } from '../TradeButton'
import { TradeObjective } from '../types/trade'

// LiFi rejects the zero address for quotes. Use a burn address so guest users can still preview exchange rates.
const GUEST_QUOTE_ADDRESS: Address = '0x000000000000000000000000000000000000dEaD'

const transport = new hl.HttpTransport()
const hlInfo = new hl.InfoClient({ transport })

export type TradeCurrentPage = 'swap' | 'modal' | 'trade'

// Get exchange rate based on trade mode
export function getExchangeRateTokenId(
  from: BridgeToken | TokenWithBalance,
  to: BridgeToken | TokenWithBalance,
  fromAssets: (BridgeToken | TokenWithBalance)[],
  toAssets: (BridgeToken | TokenWithBalance)[],
  direction?: 'from' | 'to',
): BridgeToken | undefined {
  if (direction === 'from') {
    return from as BridgeToken
  }

  if (direction === 'to') {
    return to as BridgeToken
  }

  if (from.token.symbol === TokenSymbol('USDC')) {
    if ((to as BridgeToken).tokenId !== undefined) {
      return to as BridgeToken
    }

    const asset = toAssets.find(
      (asset) => asset.token.symbol === to.token.symbol && (asset as BridgeToken).tokenId !== undefined,
    )

    if (!asset) {
      return undefined
    }

    return asset as BridgeToken
  }

  if (to.token.symbol === TokenSymbol('USDC')) {
    if ((from as BridgeToken).tokenId !== undefined) {
      return from as BridgeToken
    }

    const asset = fromAssets.find(
      (asset) => asset.token.symbol === from.token.symbol && (asset as BridgeToken).tokenId !== undefined,
    )

    if (!asset) {
      return undefined
    }

    return asset as BridgeToken
  }

  return undefined
}

export function formFormat(bnValue: BigNumber, decimals: number, isL1: boolean) {
  if (!isL1) {
    return formFormatCommon(bnValue, decimals)
  }

  const value = bnValue.toString()

  if (value && value !== '.' && value !== '-' && !/^-?\d*\.?\d*$/.test(value)) {
    return '0' // Don't update if invalid input
  }

  // Check if the input has more decimals than szDecimals
  let formattedValue = value
  if (value && value !== '.' && !Number.isNaN(Number(value))) {
    const decimalPart = value.split('.')[1]
    if (decimalPart && decimalPart.length > decimals) {
      // Only apply formFormat if decimals exceed decimals
      const numValue = Number(value)
      // Round down to decimals by using Math.floor
      const multiplier = 10 ** decimals
      const roundedValue = Math.floor(numValue * multiplier) / multiplier
      formattedValue = roundedValue.toString()
      // Remove trailing zeros after decimal point
      formattedValue = formattedValue.replace(/\.?0+$/, '')
    }
  }

  return formattedValue
}

export function getAssets(
  hyperCoreAssets: HyperCoreAsset[],
  reserves: Reserve[],
  walletInfo: MarketWalletInfo,
  evmAssets: TokenWithBalance[],
  tradingAssets: HyperCoreAsset[],
) {
  const reservesWithBalance = reserves.map((r) => ({
    token: r.token,
    balance: walletInfo.findWalletBalanceForToken(r.token),
  }))

  const filteredEvmTokens = evmAssets.filter(
    (t) => !reservesWithBalance.some((r) => r.token.symbol.toLowerCase() === t.token.symbol.toLowerCase()),
  )

  const evmTokensWithBalance = [...reservesWithBalance, ...filteredEvmTokens]

  const coreTokensWithEvmContract = hyperCoreAssets.filter((asset) => asset.evmContract)

  // Create a Set of asset names to track what we've already included
  const includedAssetNames = new Set<string>()

  // Start with core tokens that have EVM contracts
  const uniqueL1Assets: HyperCoreAsset[] = []

  for (const asset of coreTokensWithEvmContract) {
    uniqueL1Assets.push(asset)
    includedAssetNames.add(asset.name.toUpperCase())
  }

  // Add trading assets that aren't already included
  for (const asset of tradingAssets) {
    if (!includedAssetNames.has(asset.name.toUpperCase())) {
      uniqueL1Assets.push(asset)
      includedAssetNames.add(asset.name.toUpperCase())
    }
  }

  return {
    fromAssetsL1: uniqueL1Assets,
    toAssetsL1: uniqueL1Assets,
    fromAssetsEvm: evmTokensWithBalance,
    toAssetsEvm: evmTokensWithBalance,
  }
}

// Cached version of getAssets using useMemo
export function useGetAssets(
  hyperCoreAssets: HyperCoreAsset[],
  reserves: Reserve[],
  walletInfo: MarketWalletInfo,
  evmAssets: TokenWithBalance[],
  tradingAssets: HyperCoreAsset[],
) {
  return useMemo(() => {
    return getAssets(hyperCoreAssets, reserves, walletInfo, evmAssets, tradingAssets)
  }, [hyperCoreAssets, reserves, walletInfo, evmAssets, tradingAssets])
}

// Token details query hook for exchange rates
function useTokenDetails(tokenId: string | undefined, isL1Trade?: boolean) {
  return useQuery({
    queryKey: ['tokenDetails', tokenId],
    queryFn: async () => {
      if (!tokenId) return null

      return await hlInfo.tokenDetails({ tokenId: tokenId as `0x${string}` })
    },
    enabled: isL1Trade && !!tokenId,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 10 * 1000, // 10 seconds
  })
}

const BaseAssetInputSchema = z.object({
  symbol: z.string().transform(TokenSymbol),
  value: z.string().refine(() => true), // @note makes types consistent between input schemas and allows empty strings
  isL1: z.boolean(),
})

export const AssetInputSchema = BaseAssetInputSchema.extend({
  value: z.string().refine(
    (data) => {
      if (data === '') {
        return true
      }

      const value = Number.parseFloat(data)
      return !Number.isNaN(value) && value > 0
    },
    {
      message: 'Value must be greater than zero',
    },
  ),
})

export type AssetInputSchema = z.input<typeof AssetInputSchema>

function getFromFieldValidator({
  fromAssets,
  marketInfo,
}: { fromAssets: (BridgeToken | TokenWithBalance)[]; marketInfo: MarketInfo }) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const isNativeAsset = marketInfo.nativeAssetInfo.nativeAssetSymbol === field.symbol
    const value = NormalizedUnitNumber(field.value === '' ? '0' : field.value)
    const isL1 = field.isL1
    const balance =
      fromAssets.find(
        (asset) =>
          (asset.token.symbol === field.symbol && (asset as BridgeToken).tokenId !== undefined && isL1) ||
          (asset.token.symbol === field.symbol && (asset as BridgeToken).tokenId === undefined && !isL1),
      )?.balance ?? '0'

    const maxValue = getTradeMaxValue({
      balance: NormalizedUnitNumber(balance),
      isNativeAsset,
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    })

    const issue = value.gt(maxValue)

    if (issue) {
      ctx.addIssue({
        code: 'custom',
        message: isNativeAsset ? 'Not enough HYPE to cover transaction fees' : 'Insufficient balance',
        path: ['value'],
      })
    }
  })
}

function getTradeFormValidator({
  fromAssets,
  toAssets,
  marketInfo,
  tradingWithAssetsSymbols,
  currentFilterTab,
}: {
  fromAssets: (BridgeToken | TokenWithBalance)[]
  toAssets: (BridgeToken | TokenWithBalance)[]
  marketInfo: MarketInfo
  tradingWithAssetsSymbols: string[]
  currentFilterTab?: string
}) {
  return z
    .object({
      isMaxSelected: z.boolean(),
      from: getFromFieldValidator({ fromAssets, marketInfo }),
      to: BaseAssetInputSchema,
      limitPrice: z.string().optional(),
      twapHours: z.number().min(0).optional(),
      twapMinutes: z.number().min(0).optional(),
      twapRandomize: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.from.isL1 && !data.to.isL1) {
        return
      }

      // Skip validation for pure transfers (same token, just bridging between layers)
      if (data.from.symbol === data.to.symbol) {
        return
      }

      if (
        !(
          tradingWithAssetsSymbols.includes(data.from.symbol.toUpperCase()) ||
          tradingWithAssetsSymbols.includes(data.to.symbol.toUpperCase())
        )
      ) {
        return
      }

      // TWAP-specific validations
      if (currentFilterTab === 'TWAP') {
        // Validate minimum runtime of 5 minutes
        const totalMinutes = (data.twapHours || 0) * 60 + (data.twapMinutes || 0)
        if (totalMinutes < 5) {
          ctx.addIssue({
            code: 'custom',
            message: 'TWAP order must have minimum runtime of 5 minutes',
            path: ['twapHours'], // Use twapHours path
          })
        }
      }

      // Validate minimum order value - different for TWAP vs other tabs
      let minOrderValue: number
      let value: number
      let asset: BridgeToken | TokenWithBalance | undefined

      if (data.from.symbol === TokenSymbol('USDC')) {
        // When USDC is "from", use "to" value for validation
        value = Number.parseFloat(data.to.value || '0')
        asset = toAssets.find(
          (asset) => asset.token.symbol === data.to.symbol && (asset as BridgeToken).tokenId !== undefined,
        )
      } else {
        // When USDC is "to", use "from" value for validation
        value = Number.parseFloat(data.from.value || '0')
        asset = fromAssets.find(
          (asset) => asset.token.symbol === data.from.symbol && (asset as BridgeToken).tokenId !== undefined,
        )
      }
      // Only validate if we have a valid asset and USD price
      if (asset?.token.unitPriceUsd && Number(asset.token.unitPriceUsd) > 0) {
        const valueUSD = value * Number(asset.token.unitPriceUsd)

        if (currentFilterTab === 'TWAP') {
          // TWAP-specific validation: 10 USDC per minute, minimum 50 USDC
          const totalMinutes = (data.twapHours || 0) * 60 + (data.twapMinutes || 0)
          minOrderValue = Math.max(totalMinutes * 10, 50)

          if (valueUSD < minOrderValue) {
            ctx.addIssue({
              code: 'custom',
              message: `Order must have minimum value of $${minOrderValue} (${totalMinutes} minutes × $10 per minute)`,
              path: ['from', 'value'],
            })
          }
        } else {
          // Non-TWAP validation: minimum 10 USDC
          minOrderValue = 10

          if (valueUSD < minOrderValue) {
            ctx.addIssue({
              code: 'custom',
              message: `Order must have minimum value of $${minOrderValue}`,
              path: ['from', 'value'],
            })
          }
        }
      }
    })
}

export type TradeFormSchema = z.input<ReturnType<typeof getTradeFormValidator>>

export type SuccessData = TradeFormNormalizedData | null

export interface UseTradeResults {
  form: UseFormReturn<TradeFormSchema>
  actions: TradeObjective[]
  fromTokenField: UseTradeFormFieldsResults
  toTokenField: UseTradeFormFieldsResults
  fromToken: TokenWithValue
  toToken: TokenWithValue
  exchangeRate: string
  isExchangeRateLoading: boolean
  hyperliquidFee: { spotTaker: string; spotMaker: string } | null
  pageStatus: PageStatus & { resetForm: () => void; successData: SuccessData }
  guestMode: boolean
  swapQuoteData: GetPriceResult | GetPriceError | null
  openConnectModal: () => void
  swapDirection: () => void
  tradingWithAssetsSymbols: string[]
  hyperCoreAssets: HyperCoreAsset[]
  // Gas limit exceeded modal state
  showGasLimitModal: boolean
  setShowGasLimitModal: (show: boolean) => void
  gasLimitDetails: GasLimitExceededDetails | null
  isUsingBigBlocks: boolean
  isHypercoreUser: boolean
  handleGasLimitExceeded: (details: GasLimitExceededDetails) => void
  isBothDepositTokens: boolean
}

type DepositTokenType = 'hyToken' | 'purrToken' | 'eToken' | null

function getDepositTokenType(symbol: TokenSymbol): DepositTokenType {
  const s = symbol.toString()
  if (/^hy[A-Z]/.test(s)) return 'hyToken'
  if (/^purr[A-Z]/.test(s)) return 'purrToken'
  if (/^e[A-Z].*-\d+$/.test(s)) return 'eToken'
  return null
}

function nameOverride(name: string): string {
  switch (name) {
    case 'USDT0':
      return 'USD₮0'
    default:
      return name
  }
}

// Hook to fetch user's HyperLiquid spot trading fee
function useHyperLiquidFee(account?: string) {
  return useQuery({
    queryKey: ['hyperliquid-fee', account],
    queryFn: async () => {
      if (!account) return { spotTaker: '0.07', spotMaker: '0.04' }
      try {
        const userFees = await hlInfo.userFees({
          user: account as `0x${string}`,
        })

        const referralDiscountPercentage = userFees ? Number(userFees?.activeReferralDiscount) * 100 : 0

        const spotTakerFee = userFees ? Number(userFees?.userSpotCrossRate) * 100 : 0.07
        const spotMakerFee = userFees ? Number(userFees?.userSpotAddRate) * 100 : 0.04

        // Apply referral discount to both fees
        const discountedSpotTakerFee = spotTakerFee - spotTakerFee * (referralDiscountPercentage / 100)
        const discountedSpotMakerFee = spotMakerFee - spotMakerFee * (referralDiscountPercentage / 100)

        return {
          spotTaker: `${Number(discountedSpotTakerFee).toFixed(4)}`,
          spotMaker: `${Number(discountedSpotMakerFee).toFixed(4)}`,
        }
      } catch (error) {
        console.error('Failed to fetch HyperLiquid fee:', error)
        // Return default fees if API call fails
        return {
          spotTaker: '0.07',
          spotMaker: '0.04',
        }
      }
    },
    staleTime: 60000, // 1 minute
  })
}

export function useTrade(
  activeTab?: string,
  currentPage: TradeCurrentPage = 'trade',
  token?: Token,
  slippage?: number,
): UseTradeResults {
  const router = useRouter()
  const searchParams = useSearchParams()
  const paramFromToken = searchParams.get('from')
  const paramToToken = searchParams.get('to') || token?.symbol.toString() || null
  const paramFromL1 = searchParams.get('fromL1')
  const paramToL1 = searchParams.get('toL1')

  const { chainId } = usePageChainId()
  const account = useAccount()
  const guestMode = !account
  const { connectWallet } = usePrivy()
  const { balances } = useL1Balances()

  const l1Balances = useMemo(
    () => balances.map(({ coin, ...rest }) => ({ coin: nameOverride(coin), ...rest })),
    [balances],
  )
  const { assets } = useHyperCoreSpotMeta('all')

  const hyperCoreAssets = useMemo(
    () => assets.map(({ name, ...rest }) => ({ name: nameOverride(name), ...rest })),
    [assets],
  )

  const [successData, setSuccessData] = useState<SuccessData>(null)
  const { marketInfo } = useMarketInfo({ chainId })
  const { markets } = getChainConfigEntry(chainId)
  const { nativeAssetInfo } = markets ?? {}
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const walletInfo = useMarketWalletInfoForMarketQuery({ chainId, marketInfo })
  const formRef = useRef<UseFormReturn<TradeFormSchema> | null>(null)

  // Gas limit exceeded modal state
  const [showGasLimitModal, setShowGasLimitModal] = useState(false)
  const [gasLimitDetails, setGasLimitDetails] = useState<GasLimitExceededDetails | null>(null)

  // Big blocks and Hypercore user status
  const { data: isUsingBigBlocks } = useIsUsingBigBlocks()
  const { isHypercoreUser } = useIsHypercoreUser()

  // Callback when gas limit would be exceeded
  const handleGasLimitExceeded = useCallback((details: GasLimitExceededDetails) => {
    setGasLimitDetails(details)
    setShowGasLimitModal(true)
  }, [])

  // Fetch user's HyperLiquid spot trading fee
  const { data: hyperLiquidFee } = useHyperLiquidFee(account)

  assert(nativeAssetInfo, 'Native asset info is required for swap')
  const { nativeAssetSymbol, wrappedNativeAssetSymbol } = nativeAssetInfo

  const { tokensInfo } = useSwapEvmTokens({
    nativeAssetSymbol,
    wrappedNativeAssetSymbol,
  })

  const tradingWithAssets = useMemo(() => {
    // Collect all unique trading symbols from tradingWith arrays
    const allTradingSymbols = new Set<string>()

    // Collect all unique symbols from tradingWith arrays
    for (const asset of hyperCoreAssets) {
      if (asset.tradingWith && Array.isArray(asset.tradingWith)) {
        for (const tradingSymbol of asset.tradingWith) {
          allTradingSymbols.add(tradingSymbol)
        }
      }
    }

    // Get all assets that match the trading symbols
    return {
      assets: hyperCoreAssets.filter((asset) => allTradingSymbols.has(asset.name.toUpperCase())),
      symbols: Array.from(allTradingSymbols),
    }
  }, [hyperCoreAssets])

  // Memoize reserves array to prevent new reference on every render,
  // which would invalidate all downstream asset memos (expensive with 800+ tokens)
  const allReserves = useMemo(
    () => [...marketInfo.reserves, marketInfo.findOneReserveBySymbol(nativeAssetInfo.nativeAssetSymbol)],
    [marketInfo, nativeAssetInfo],
  )

  const { fromAssetsL1, toAssetsL1, fromAssetsEvm, toAssetsEvm } = useGetAssets(
    hyperCoreAssets,
    allReserves,
    walletInfo,
    tokensInfo.all(),
    tradingWithAssets.assets,
  )

  // Initialize with full assets (not filtered) - using useMemo to prevent recalculation
  const fullFromAssets = useMemo(
    () => getBridgeAssetsHyperCore(allReserves, fromAssetsL1, fromAssetsEvm, tokensInfo, l1Balances, walletInfo),
    [allReserves, fromAssetsL1, fromAssetsEvm, tokensInfo, l1Balances, walletInfo],
  )

  const fullToAssets = useMemo(
    () => getBridgeAssetsHyperCore(allReserves, toAssetsL1, toAssetsEvm, tokensInfo, l1Balances, walletInfo),
    [allReserves, toAssetsL1, toAssetsEvm, tokensInfo, l1Balances, walletInfo],
  )

  // Use state to manage the current tab for asset filtering
  const [currentFilterTab, setCurrentFilterTab] = useState<string | undefined>(activeTab)

  // Memoize L1-only assets for Limit/TWAP mode (no EVM assets)
  const l1OnlyAssets = useMemo(
    () => getBridgeAssetsHyperCore(allReserves, fromAssetsL1, [], tokensInfo, l1Balances, walletInfo),
    [allReserves, fromAssetsL1, tokensInfo, l1Balances, walletInfo],
  )

  // Filter assets based on the current filter tab
  const rawFromAssets = currentFilterTab === 'Limit' || currentFilterTab === 'TWAP' ? l1OnlyAssets : fullFromAssets
  const rawToAssets = currentFilterTab === 'Limit' || currentFilterTab === 'TWAP' ? l1OnlyAssets : fullToAssets

  // State for on-demand Lifi token balances, keyed by lowercase address
  const [lifiBalances, setLifiBalances] = useState<Record<string, NormalizedUnitNumber>>({})

  // Patch raw assets with any fetched Lifi balances
  const fromAssets = useMemo(() => {
    if (Object.keys(lifiBalances).length === 0) return rawFromAssets
    return rawFromAssets.map((a) => {
      if ((a as BridgeToken).tokenId !== undefined) return a
      const balance = lifiBalances[a.token.address.toLowerCase()]
      return balance !== undefined ? { ...a, balance } : a
    })
  }, [rawFromAssets, lifiBalances])

  const toAssets = useMemo(() => {
    if (Object.keys(lifiBalances).length === 0) return rawToAssets
    return rawToAssets.map((a) => {
      if ((a as BridgeToken).tokenId !== undefined) return a
      const balance = lifiBalances[a.token.address.toLowerCase()]
      return balance !== undefined ? { ...a, balance } : a
    })
  }, [rawToAssets, lifiBalances])

  const form = useForm<TradeFormSchema>({
    resolver: zodResolver(
      getTradeFormValidator({
        fromAssets,
        toAssets,
        marketInfo,
        tradingWithAssetsSymbols: tradingWithAssets.symbols,
        currentFilterTab: activeTab || '',
      }),
      undefined,
      { raw: true },
    ),
    defaultValues: {
      ...getDefaultFormValues(fromAssets, toAssets, currentFilterTab, {
        paramFromToken,
        paramToToken,
        paramFromL1,
        paramToL1,
      }),
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })

  // Store form in ref
  formRef.current = form

  // Handle tab switching with intelligent token positioning
  useEffect(() => {
    if (
      (activeTab === 'Limit' || activeTab === 'TWAP') &&
      currentFilterTab !== 'Limit' &&
      currentFilterTab !== 'TWAP'
    ) {
      // Get available L1 assets for limit mode
      const limitAssets = getBridgeAssetsHyperCore(
        allReserves,
        hyperCoreAssets,
        [], // No EVM assets in limit mode
        tokensInfo,
        l1Balances,
        walletInfo,
      ).filter((asset) => 'tokenId' in asset && asset.tokenId !== undefined)

      const currentValues = form.getValues()
      const currentFromSymbol = currentValues.from.symbol
      const currentToSymbol = currentValues.to.symbol
      const currentFromIsL1 = currentValues.from.isL1
      const currentToIsL1 = currentValues.to.isL1

      // Check if current tokens are L1 compatible
      const fromTokenIsL1Available = limitAssets.some(
        (asset) => asset.token.symbol === currentFromSymbol && currentFromIsL1,
      )
      const toTokenIsL1Available = limitAssets.some((asset) => asset.token.symbol === currentToSymbol && currentToIsL1)

      let finalFromSymbol = currentFromSymbol
      let finalToSymbol = currentToSymbol
      let shouldUpdateTokens = false

      // Simple logic: if one token is L1, set the opposite to USDC
      if (fromTokenIsL1Available && currentFromSymbol !== TokenSymbol('USDC')) {
        // FROM token is L1 and not USDC, set TO token to USDC
        finalFromSymbol = currentFromSymbol
        finalToSymbol = TokenSymbol('USDC')
        shouldUpdateTokens = true
      } else if (toTokenIsL1Available && currentToSymbol !== TokenSymbol('USDC')) {
        // TO token is L1 and not USDC, set FROM token to USDC
        finalFromSymbol = TokenSymbol('USDC')
        finalToSymbol = currentToSymbol
        shouldUpdateTokens = true
      } else if (fromTokenIsL1Available && currentFromSymbol === TokenSymbol('USDC')) {
        // FROM token is USDC L1, find another L1 token for TO
        finalFromSymbol = currentFromSymbol
        finalToSymbol =
          limitAssets.find((asset) => asset.token.symbol !== TokenSymbol('USDC'))?.token.symbol || TokenSymbol('HYPE')
        shouldUpdateTokens = true
      } else if (toTokenIsL1Available && currentToSymbol === TokenSymbol('USDC')) {
        // TO token is USDC L1, find another L1 token for FROM
        finalFromSymbol =
          limitAssets.find((asset) => asset.token.symbol !== TokenSymbol('USDC'))?.token.symbol || TokenSymbol('HYPE')
        finalToSymbol = currentToSymbol
        shouldUpdateTokens = true
      } else {
        // Neither token is L1 compatible, use defaults
        finalFromSymbol =
          limitAssets.find((asset) => asset.token.symbol !== TokenSymbol('USDC'))?.token.symbol || TokenSymbol('HYPE')
        finalToSymbol = TokenSymbol('USDC')
        shouldUpdateTokens = true
      }

      // Find the actual assets
      const finalFromAsset = limitAssets.find((asset) => asset.token.symbol === finalFromSymbol)
      const finalToAsset = limitAssets.find((asset) => asset.token.symbol === finalToSymbol)

      // Apply the changes
      if (shouldUpdateTokens || !fromTokenIsL1Available || !toTokenIsL1Available) {
        if (finalFromAsset) {
          form.setValue('from.symbol', finalFromAsset.token.symbol)
          form.setValue('from.isL1', true)
          form.setValue('from.value', '')
        }

        if (finalToAsset) {
          form.setValue('to.symbol', finalToAsset.token.symbol)
          form.setValue('to.isL1', true)
          form.setValue('to.value', '')
        }

        // Clear limit price when tokens change
        form.setValue('limitPrice', '', { shouldValidate: false })

        // Update URL to reflect new token selection
        if (finalFromAsset && finalToAsset) {
          if (currentPage !== 'modal') {
            router.replace(
              `/${currentPage}?from=${finalFromAsset.token.symbol}&to=${finalToAsset.token.symbol}&fromL1=true&toL1=true`,
              { scroll: false },
            )
          }
        }
      }

      // Update the filter tab to trigger asset filtering
      setCurrentFilterTab(activeTab)
    } else if (
      activeTab !== 'Limit' &&
      activeTab !== 'TWAP' &&
      (currentFilterTab === 'Limit' || currentFilterTab === 'TWAP')
    ) {
      // Switch back to market mode
      setCurrentFilterTab(activeTab)
    }
  }, [
    activeTab,
    currentFilterTab,
    hyperCoreAssets,
    allReserves,
    tokensInfo,
    l1Balances,
    walletInfo,
    form,
    router,
    currentPage,
  ])

  const rawFormValues = form.watch()

  // Fetch balance on-demand for selected Lifi-only EVM tokens
  const reserveSymbols = useMemo(
    () => new Set(marketInfo.reserves.map((r) => r.token.symbol.toLowerCase())),
    [marketInfo.reserves],
  )

  const selectedFromAsset = !rawFormValues.from.isL1
    ? fromAssets.find((a) => a.token.symbol === rawFormValues.from.symbol && (a as BridgeToken).tokenId === undefined)
    : undefined
  const selectedToAsset = !rawFormValues.to.isL1
    ? toAssets.find((a) => a.token.symbol === rawFormValues.to.symbol && (a as BridgeToken).tokenId === undefined)
    : undefined

  const isFromLifiOnly = !!selectedFromAsset && !reserveSymbols.has(rawFormValues.from.symbol.toLowerCase())
  const isToLifiOnly = !!selectedToAsset && !reserveSymbols.has(rawFormValues.to.symbol.toLowerCase())

  const { data: fetchedFromBalance } = useTokenBalance({
    tokenAddress: selectedFromAsset?.token.address,
    decimals: selectedFromAsset?.token.decimals ?? 18,
    chainId,
    enabled: isFromLifiOnly,
  })

  const { data: fetchedToBalance } = useTokenBalance({
    tokenAddress: selectedToAsset?.token.address,
    decimals: selectedToAsset?.token.decimals ?? 18,
    chainId,
    enabled: isToLifiOnly,
  })

  // Update lifiBalances state when on-demand balances arrive
  // Use string values in deps to avoid BigNumber reference inequality triggering infinite loops
  const selectedFromAddress = selectedFromAsset?.token.address
  const selectedToAddress = selectedToAsset?.token.address
  const fetchedFromBalanceStr = fetchedFromBalance?.toFixed()
  const fetchedToBalanceStr = fetchedToBalance?.toFixed()

  useEffect(() => {
    if (fetchedFromBalanceStr !== undefined && selectedFromAddress && isFromLifiOnly) {
      setLifiBalances((prev) => {
        const key = selectedFromAddress.toLowerCase()
        if (prev[key]?.toFixed() === fetchedFromBalanceStr) return prev
        return { ...prev, [key]: NormalizedUnitNumber(fetchedFromBalanceStr) }
      })
    }
  }, [fetchedFromBalanceStr, selectedFromAddress, isFromLifiOnly])

  useEffect(() => {
    if (fetchedToBalanceStr !== undefined && selectedToAddress && isToLifiOnly) {
      setLifiBalances((prev) => {
        const key = selectedToAddress.toLowerCase()
        if (prev[key]?.toFixed() === fetchedToBalanceStr) return prev
        return { ...prev, [key]: NormalizedUnitNumber(fetchedToBalanceStr) }
      })
    }
  }, [fetchedToBalanceStr, selectedToAddress, isToLifiOnly])

  const formValues = normalizeFormValues(rawFormValues, fromAssets, toAssets)

  const isBothDepositTokens = useMemo(() => {
    if (rawFormValues.from.isL1 || rawFormValues.to.isL1) return false
    const fromType = getDepositTokenType(TokenSymbol(rawFormValues.from.symbol))
    const toType = getDepositTokenType(TokenSymbol(rawFormValues.to.symbol))
    return fromType !== null && fromType === toType
  }, [rawFormValues.from.symbol, rawFormValues.to.symbol, rawFormValues.from.isL1, rawFormValues.to.isL1])

  const actions = useCreateObjectives(formValues, fromAssets, toAssets)

  const { isL1Trade, isEvmTrade, isDualL1Trade } = getTradeType(
    formValues.from,
    formValues.to,
    nativeAssetInfo.nativeAssetSymbol,
    nativeAssetInfo.wrappedNativeAssetSymbol,
    tradingWithAssets.symbols,
  )

  const { data: tokenDetails, isLoading: tokenDetailsLoading } = useTokenDetails(
    getExchangeRateTokenId(formValues.from, formValues.to, fromAssets, toAssets, isDualL1Trade ? 'from' : undefined)
      ?.tokenId,
    isL1Trade || isDualL1Trade,
  )

  const { data: toTokenDetails, isLoading: toTokenDetailsLoading } = useTokenDetails(
    getExchangeRateTokenId(formValues.from, formValues.to, fromAssets, toAssets, 'to')?.tokenId,
    isDualL1Trade,
  )

  const toTokenEVM =
    (formValues.to as BridgeToken).tokenId === undefined
      ? formValues.to.token
      : toAssets.find(
          (asset) => asset.token.symbol === formValues.to.token.symbol && (asset as BridgeToken).tokenId === undefined,
        )?.token

  const fromTokenEVM =
    (formValues.from as BridgeToken).tokenId === undefined
      ? formValues.from.token
      : fromAssets.find(
          (asset) =>
            asset.token.symbol === formValues.from.token.symbol && (asset as BridgeToken).tokenId === undefined,
        )?.token

  const priceInputAmount = fromTokenEVM?.toBaseUnit(
    formValues.from.value.toString() === '0' || formValues.from.value.toString() === ''
      ? NormalizedUnitNumber('1')
      : formValues.from.value,
  )

  const { debouncedValue: debouncedPriceInput, isDebouncing: isPriceDebouncing } = useDebounce(
    {
      inputToken: fromTokenEVM?.address,
      outputToken: toTokenEVM?.address,
      inputAmount: priceInputAmount,
    },
    `${fromTokenEVM?.address}-${toTokenEVM?.address}-${priceInputAmount?.toString()}`,
    { delay: 500 },
  )

  const { price: priceData, isLoading: priceLoading } = useGetPrice({
    inputToken: debouncedPriceInput.inputToken,
    outputToken: debouncedPriceInput.outputToken,
    account: account || GUEST_QUOTE_ADDRESS,
    inputAmount: debouncedPriceInput.inputAmount,
    enabled: isEvmTrade && !isPriceDebouncing,
    slippage: slippage != null ? Math.round(slippage * 10000) : undefined,
    resetErrors: () => {
      form.clearErrors()
    },
  })

  // Check if priceData matches current form values to avoid using stale data
  // Use fromTokenEVM/toTokenEVM addresses since those are what we pass to useGetPrice
  const isPriceDataValid =
    priceData &&
    'result' in priceData &&
    fromTokenEVM &&
    toTokenEVM &&
    priceData.result.inputToken.toLowerCase() === fromTokenEVM.address.toLowerCase() &&
    priceData.result.outputToken.toLowerCase() === toTokenEVM.address.toLowerCase()

  const quoteOutputAmount = isPriceDataValid ? priceData.result.effectiveOutputAmount : null

  const toAmount = quoteOutputAmount ? toTokenEVM?.fromBaseUnit(BaseUnitNumber(quoteOutputAmount)) : null

  const lastEditedFieldRef = useRef<'from' | 'to' | null>(null)
  const fromAssetsRef = useRef(fromAssets)
  const toAssetsRef = useRef(toAssets)
  const previousExchangeRateRef = useRef<string>('N/A')

  function calculateL1ExchangeRate(tokenDetails: hl.TokenDetails | null | undefined, fromToken: Token): string {
    if (!tokenDetails?.markPx) return 'N/A'

    // If USDC is the "from" token, we need to invert the price
    // markPx is always in terms of USDC (e.g., HYPE/USDC)
    // So when USDC is "from", we need 1/markPx to get USDC/HYPE
    if (fromToken.symbol.toUpperCase() !== tokenDetails.name.toUpperCase()) {
      const invertedPrice = 1 / Number(tokenDetails.markPx)
      return invertedPrice.toFixed(fromToken.decimals)
    }

    // If USDC is the "to" token, use the price as is
    return tokenDetails.markPx
  }

  const currentExchangeRate = isDualL1Trade
    ? toTokenDetails?.markPx && tokenDetails?.markPx
      ? formFormat(
          NormalizedUnitNumber(tokenDetails.markPx).dividedBy(NormalizedUnitNumber(toTokenDetails.markPx)),
          formValues.to.token.decimals,
          true,
        )
      : 'N/A'
    : (isL1Trade
        ? calculateL1ExchangeRate(tokenDetails, formValues.from.token)
        : isEvmTrade
          ? toAmount
            ? formFormat(
                NormalizedUnitNumber(toAmount).dividedBy(
                  Number(formValues.from.value) === 0
                    ? NormalizedUnitNumber(1)
                    : NormalizedUnitNumber(formValues.from.value),
                ),
                formValues.to.token.decimals,
                (formValues.to as BridgeToken).tokenId !== undefined,
              )
            : 'N/A'
          : '1') || 'N/A'

  // Store previous exchange rate when we have a valid new one
  if (currentExchangeRate !== 'N/A' && currentExchangeRate !== '1') {
    previousExchangeRateRef.current = currentExchangeRate
  }

  const isExchangeRateLoading = tokenDetailsLoading || priceLoading || isPriceDebouncing || toTokenDetailsLoading

  // Use previous exchange rate when loading, otherwise use current
  const exchangeRate =
    isExchangeRateLoading && previousExchangeRateRef.current !== 'N/A'
      ? previousExchangeRateRef.current
      : currentExchangeRate

  // Auto-fill limit price when switching to limit mode or when exchange rate becomes available
  useEffect(() => {
    if (
      currentFilterTab === 'Limit' &&
      exchangeRate &&
      exchangeRate !== 'N/A' &&
      !isExchangeRateLoading &&
      !form.getValues('limitPrice')
    ) {
      form.setValue('limitPrice', exchangeRate, { shouldValidate: false })
    }
  }, [currentFilterTab, exchangeRate, isExchangeRateLoading, form])

  // Track token changes and clear limit price when tokens change in limit mode
  const currentTokenPairRef = useRef<string>('')
  // Derive from rawFormValues (already watched) instead of separate form.watch() calls
  const fromSymbol = rawFormValues.from.symbol
  const toSymbol = rawFormValues.to.symbol
  const fromIsL1 = rawFormValues.from.isL1
  const toIsL1 = rawFormValues.to.isL1

  useEffect(() => {
    if (currentFilterTab === 'Limit' || currentFilterTab === 'TWAP') {
      const currentTokenPair = `${fromSymbol}-${toSymbol}-${fromIsL1}-${toIsL1}`

      if (currentTokenPairRef.current && currentTokenPairRef.current !== currentTokenPair) {
        // Token pair changed, clear limit price to avoid stale pricing
        if (currentFilterTab === 'Limit') {
          form.setValue('limitPrice', '', { shouldValidate: false })
        }
        // For TWAP, we might want to clear other fields if needed
      }
      currentTokenPairRef.current = currentTokenPair
    }
  }, [currentFilterTab, fromSymbol, toSymbol, fromIsL1, toIsL1, form])

  // Update asset refs when they change
  useEffect(() => {
    fromAssetsRef.current = fromAssets
  }, [fromAssets])

  useEffect(() => {
    toAssetsRef.current = toAssets
  }, [toAssets])

  function recalculateExchangeRate(exchangeRate: string) {
    if (!formRef.current) {
      return
    }

    const lastEditedField = lastEditedFieldRef.current

    if (!lastEditedField) {
      return
    }

    const lastEditedValue = formRef.current.getValues(`${lastEditedField}.value`)

    // Early return if no value to calculate
    if (!lastEditedValue) {
      formRef.current.setValue('from.value', '', { shouldValidate: false })
      formRef.current.setValue('to.value', '', { shouldValidate: false })
      return
    }

    const toSymbol = formRef.current.getValues('to.symbol')
    const fromSymbol = formRef.current.getValues('from.symbol')
    const toIsL1 = formRef.current.getValues('to.isL1')
    const fromIsL1 = formRef.current.getValues('from.isL1')

    // Validate that the exchange rate belongs to the current token pair
    const currentFromSymbol = formValues.from.token.symbol
    const currentToSymbol = formValues.to.token.symbol
    const currentFromIsL1 = (formValues.from as BridgeToken).tokenId !== undefined
    const currentToIsL1 = (formValues.to as BridgeToken).tokenId !== undefined

    // Check if the form values have changed since the exchange rate was calculated
    if (
      fromSymbol !== currentFromSymbol ||
      toSymbol !== currentToSymbol ||
      fromIsL1 !== currentFromIsL1 ||
      toIsL1 !== currentToIsL1
    ) {
      // Exchange rate is stale, don't use it for calculations
      console.warn('Exchange rate is stale for current token pair, skipping recalculation')
      return
    }

    const toAsset = toAssetsRef.current.find(
      (asset) => asset.token.symbol === toSymbol && ((asset as BridgeToken).tokenId !== undefined) === toIsL1,
    )
    const fromAsset = fromAssetsRef.current.find(
      (asset) => asset.token.symbol === fromSymbol && ((asset as BridgeToken).tokenId !== undefined) === fromIsL1,
    )

    const inputAmount = Number(lastEditedValue)
    const rate = Number(exchangeRate)
    let calculatedAmount: number

    if (lastEditedField === 'from') {
      calculatedAmount = inputAmount * rate

      if (toAsset) {
        const formattedAmount = formFormat(NormalizedUnitNumber(calculatedAmount), toAsset.token.decimals, toIsL1)
        formRef.current.setValue('to.value', formattedAmount, { shouldValidate: false })
      }
    } else if (lastEditedField === 'to') {
      calculatedAmount = inputAmount / rate

      if (fromAsset) {
        const formattedAmount = formFormat(NormalizedUnitNumber(calculatedAmount), fromAsset.token.decimals, fromIsL1)
        formRef.current.setValue('from.value', formattedAmount, { shouldValidate: false })
      }
    }
  }

  // Recalculate counterpart field when exchange rate changes, preserving last edited field
  // Skip when loading to avoid flip-flopping between stale/current rates
  // biome-ignore lint/correctness/useExhaustiveDependencies: only recalculate on fresh exchange rate changes
  useEffect(() => {
    if (isExchangeRateLoading) return
    if (exchangeRate && Number(exchangeRate) > 0 && lastEditedFieldRef.current && formRef.current) {
      // In limit mode, don't recalculate based on exchangeRate - use limitPrice instead
      const rateToUse = currentFilterTab === 'Limit' ? form.getValues('limitPrice') : exchangeRate
      if (rateToUse && Number(rateToUse) > 0) {
        recalculateExchangeRate(rateToUse)
      }
    }
  }, [exchangeRate])

  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function createCustomOnChange(fieldName: 'from' | 'to') {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      form.clearErrors(fieldName)
      form.clearErrors('to')

      form.setValue(`${fieldName}.value`, value, { shouldValidate: false })

      // Track which field was last manually edited
      lastEditedFieldRef.current = fieldName

      // Auto-calculate the counterpart field immediately when either field changes
      const rateToUse = currentFilterTab === 'Limit' ? form.getValues('limitPrice') : exchangeRate
      if (rateToUse && Number(rateToUse) > 0) {
        recalculateExchangeRate(rateToUse)
      }

      // Debounce validation to avoid running zod resolver on every keystroke
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current)
      }
      validationTimerRef.current = setTimeout(() => {
        form.trigger().catch(console.error)
      }, 300)
    }
  }

  const fromTokenField = useTradeFormFields({
    form,
    assets: fromAssets,
    fieldName: 'from',
    counterpartFieldName: 'to',
    tokensInfo,
    onChange: createCustomOnChange('from'),
    l1Balances,
    exchangeRate,
    toAssetsRef,
    marketInfo,
    lastEditedFieldRef,
    currentTab: currentFilterTab,
    currentPage,
  })

  const toTokenField = useTradeFormFields({
    form,
    assets: toAssets,
    fieldName: 'to',
    counterpartFieldName: 'from',
    tokensInfo,
    onChange: createCustomOnChange('to'),
    l1Balances,
    exchangeRate,
    toAssetsRef,
    marketInfo,
    lastEditedFieldRef,
    currentTab: currentFilterTab,
    currentPage,
  })

  function handleTradeDirection(): void {
    // Get current form values
    const currentFromSymbol = form.getValues('from.symbol')
    const currentToSymbol = form.getValues('to.symbol')
    const currentFromValue = form.getValues('from.value')
    const currentToValue = form.getValues('to.value')
    const currentFromIsL1 = form.getValues('from.isL1')
    const currentToIsL1 = form.getValues('to.isL1')

    // Reset max selected state when swapping direction
    form.setValue('isMaxSelected', false, { shouldValidate: false })

    // Swap the assets when trade mode changes
    form.setValue('from.symbol', currentToSymbol)
    form.setValue('to.symbol', currentFromSymbol)
    form.setValue('from.value', currentToValue)
    form.setValue('to.value', currentFromValue)
    form.setValue('from.isL1', currentToIsL1)
    form.setValue('to.isL1', currentFromIsL1)

    // Clear limit price when swapping direction in limit mode
    if (currentFilterTab === 'Limit') {
      form.setValue('limitPrice', '', { shouldValidate: false })
    }

    if (currentPage !== 'modal') {
      router.replace(
        `/${currentPage}?from=${currentToSymbol}&to=${currentFromSymbol}&fromL1=${currentToIsL1}&toL1=${currentFromIsL1}`,
        {
          scroll: false,
        },
      )
    }

    // Re-trigger validation
    form.trigger().catch(console.error)
  }

  return {
    form,
    actions,
    fromTokenField,
    toTokenField,
    fromToken: formValues.from,
    toToken: formValues.to,
    exchangeRate,
    isExchangeRateLoading,
    hyperliquidFee: hyperLiquidFee ?? null,
    swapQuoteData: priceData ?? null,
    tradingWithAssetsSymbols: tradingWithAssets.symbols,
    pageStatus: {
      state: pageStatus,
      actionsEnabled:
        formValues.from.value.gt(0) && form.formState.isValid && !isExchangeRateLoading && exchangeRate !== 'N/A',
      successData,
      goToSuccessScreen: () => {
        setSuccessData({ ...formValues })
        setPageStatus('success')

        form.reset(
          getDefaultFormValues(fromAssets, toAssets, currentFilterTab, {
            paramFromToken,
            paramToToken,
            paramFromL1,
            paramToL1,
          }),
        )
        void queryClient.refetchQueries({
          queryKey: ['swap-tokens', 'lifi'],
        })
        void queryClient.refetchQueries({
          queryKey: l1BalancesQueryKey({
            account,
          }),
        })

        void queryClient.invalidateQueries({
          queryKey: ['openOrders', account],
        })

        void queryClient.invalidateQueries({
          queryKey: ['twapOrders', account],
        })

        void queryClient.invalidateQueries({
          queryKey: marketBalancesQueryKey({ account, chainId }),
        })

        void queryClient.invalidateQueries({
          queryKey: ['token-balance'],
        })
      },
      resetForm: () => {
        form.reset(
          getDefaultFormValues(fromAssets, toAssets, currentFilterTab, {
            paramFromToken,
            paramToToken,
            paramFromL1,
            paramToL1,
          }),
        )
        setPageStatus('form')
        setSuccessData(null)
      },
    },
    guestMode,
    swapDirection: handleTradeDirection,
    openConnectModal: connectWallet,
    hyperCoreAssets,
    // Gas limit exceeded modal state
    showGasLimitModal,
    setShowGasLimitModal,
    gasLimitDetails,
    isUsingBigBlocks: isUsingBigBlocks ?? false,
    isHypercoreUser,
    handleGasLimitExceeded,
    isBothDepositTokens,
  }
}

export type BridgeToken = SpotToken & {
  token: Token
  l1Balance: NormalizedUnitNumber
  systemAddress: string
  balance: NormalizedUnitNumber
  value: NormalizedUnitNumber
  tokenId: string
  universeIndex: number
  midPrice: string
  depositContract?: string
}
export interface UseDebouncedFormValuesArgs {
  form: UseFormReturn<TradeFormSchema>
  fromAssets: (BridgeToken | TokenWithBalance)[]
  toAssets: (BridgeToken | TokenWithBalance)[]
}
export interface UseDebouncedDialogFormValuesResult {
  debouncedFormValues: TradeFormNormalizedData
  isFormValid: boolean
  isDebouncing: boolean
}
export function useDebouncedDialogFormValues({
  form,
  fromAssets,
  toAssets,
}: UseDebouncedFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = normalizeFormValues(form.watch(), fromAssets, toAssets)
  const isFormValid = form.formState.isValid
  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues, isFormValid },
    getNormalizedFormValuesKey(formValues) + isFormValid.toString(),
  )

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

function getNormalizedFormValuesKey(values: TradeFormNormalizedData): string {
  return [
    values.from.token.address,
    values.from.value.toFixed(),
    (values.from as BridgeToken).tokenId !== undefined ? (values.from as BridgeToken).tokenId : '0',
  ].join('-')
}

function getTokenFromSpotToken(
  reserves: Reserve[],
  coreAsset: HyperCoreAsset,
  _tokensInfo: TokensInfo,
  l1Balances: SpotBalance[],
  _walletInfo: MarketWalletInfo,
): BridgeToken {
  const priceInUSD = reserves
    .find((reserve) => reserve.token.symbol.toUpperCase() === TokenSymbol(coreAsset.name))
    ?.priceInUSD.toString()

  const exchangeRate = queryClient.getQueryData<hl.TokenDetails>(['tokenDetails', coreAsset.tokenId])

  const token = new Token({
    symbol: TokenSymbol(coreAsset.name),
    decimals: coreAsset.weiDecimals + (coreAsset?.evmContract?.evm_extra_wei_decimals ?? 0),
    address: CheckedAddress(coreAsset.evmContract?.address ?? zeroAddress),
    unitPriceUsd: exchangeRate?.markPx || priceInUSD || coreAsset.midPrice || '0',
    name: coreAsset.fullName || coreAsset.name,
    isAToken: false,
  })

  const spotToken: BridgeToken = {
    ...coreAsset,
    token,
    evmContract: coreAsset.evmContract,
    systemAddress: coreAsset.systemAddress,
    l1Balance: NormalizedUnitNumber(l1Balances.find(({ coin }) => coin === coreAsset.name)?.total ?? '0'),
    balance: NormalizedUnitNumber(l1Balances.find(({ coin }) => coin === coreAsset.name)?.total ?? '0'),
    value: NormalizedUnitNumber(0),
  }
  return spotToken
}

export function hasL1Balance(r: HyperCoreAsset, l1Balances: SpotBalance[]): boolean {
  return NormalizedUnitNumber(l1Balances.find(({ coin }) => coin === r.name)?.total ?? '0').gt(0)
}

export function hasEvmBalance(r: TokenWithBalance, tokensInfo: TokensInfo, walletInfo: MarketWalletInfo): boolean {
  return (
    !!tokensInfo.findOneTokenWithBalanceBySymbolCaseInsensitive(TokenSymbol(r.token.symbol))?.balance.gt(0) ||
    walletInfo.findWalletBalanceForSymbol(TokenSymbol(r.token.symbol)).gt(0)
  )
}

export function getBridgeAssetsHyperCore(
  reserves: Reserve[],
  hyperCoreAssets: HyperCoreAsset[],
  evmAssets: TokenWithBalance[],
  tokensInfo: TokensInfo,
  l1Balances: SpotBalance[],
  walletInfo: MarketWalletInfo,
): (BridgeToken | TokenWithBalance)[] {
  const hyperCoreAssetsWithBalance = hyperCoreAssets.map((coreAsset) =>
    getTokenFromSpotToken(reserves, coreAsset, tokensInfo, l1Balances, walletInfo),
  )

  const evmAssetsWithCorePrice: TokenWithBalance[] = evmAssets.map((evmAsset) => {
    const priceInUSD = reserves
      .find((reserve) => reserve.token.symbol.toUpperCase() === evmAsset.token.symbol.toUpperCase())
      ?.priceInUSD.toString()

    const coreAsset = hyperCoreAssets.find((asset) => asset.name.toUpperCase() === evmAsset.token.symbol.toUpperCase())

    return {
      ...evmAsset,
      token: new Token({
        ...evmAsset.token,
        unitPriceUsd: priceInUSD || coreAsset?.midPrice || evmAsset.token.unitPriceUsd.toFixed() || '0',
      }),
    }
  })

  // Deduplicate by address + symbol + isL1 to prevent duplicate React keys
  const seen = new Set<string>()
  const deduplicated = [...hyperCoreAssetsWithBalance, ...evmAssetsWithCorePrice].filter((asset) => {
    const isL1 = (asset as BridgeToken).tokenId !== undefined
    const key = `${asset.token.address}-${asset.token.symbol}-${isL1}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  return deduplicated.sort((a: BridgeToken | TokenWithBalance, b: BridgeToken | TokenWithBalance) => {
    if (a.token.symbol === 'HYPE' && b.token.symbol !== 'HYPE') return -1
    if (a.token.symbol !== 'HYPE' && b.token.symbol === 'HYPE') return 1

    // Sort by unitPriceUsd multiplied by balance
    const aPrice = Number(a.token.unitPriceUsd)
    const bPrice = Number(b.token.unitPriceUsd)
    const aBalance = Number(a.balance)
    const bBalance = Number(b.balance)

    // Calculate total value (price * balance)
    const aValue = aPrice * aBalance
    const bValue = bPrice * bBalance

    // Priority 1: Tokens with balance come first (regardless of price)
    if (aBalance > 0 && bBalance === 0) return -1 // A has balance, B doesn't
    if (aBalance === 0 && bBalance > 0) return 1 // B has balance, A doesn't

    // Priority 2: Both have balances - sort by price status and value
    if (aBalance > 0 && bBalance > 0) {
      // Both have balances
      if (aPrice > 0 && bPrice === 0) return -1 // A has price, B doesn't
      if (aPrice === 0 && bPrice > 0) return 1 // B has price, A doesn't
      if (aPrice > 0 && bPrice > 0) return bValue - aValue // Both have prices, sort by value
      if (aPrice === 0 && bPrice === 0) return bBalance - aBalance // Both no price, sort by balance
    }

    // Priority 3: Both have no balance - sort by price status
    if (aBalance === 0 && bBalance === 0) {
      if (aPrice > 0 && bPrice === 0) return -1 // A has price, B doesn't
      if (aPrice === 0 && bPrice > 0) return 1 // B has price, A doesn't
      return bValue - aValue // Sort by value or 0 if both no price
    }

    // Fallback (shouldn't reach here)
    return 0
  })
}

// Shared filtering function for trade assets
function filterTradeAssets(
  assets: (BridgeToken | TokenWithBalance)[],
  counterpartSymbol: string | null,
  currentFilterTab?: string,
): (BridgeToken | TokenWithBalance)[] {
  if (currentFilterTab !== 'Limit' && currentFilterTab !== 'TWAP') {
    return assets
  }

  return assets.filter((asset) => {
    // Only show L1 assets in limit/TWAP mode
    if ((asset as BridgeToken).tokenId === undefined) {
      return false
    }

    // If counterparty selected USDC (L1), display all L1 assets except USDC
    if (counterpartSymbol === 'USDC') {
      return asset.token.symbol !== TokenSymbol('USDC')
    }

    // If counterparty selected L1 token (not USDC), show USDC and other L1 tokens
    return asset.token.symbol === TokenSymbol('USDC')
  })
}

export function getDefaultFormValues(
  fromAssets: (BridgeToken | TokenWithBalance)[],
  toAssets: (BridgeToken | TokenWithBalance)[],
  currentFilterTab: string | undefined,
  {
    paramFromToken,
    paramToToken,
    paramFromL1,
    paramToL1,
  }: {
    paramFromToken: string | null
    paramToToken: string | null
    paramFromL1: string | null
    paramToL1: string | null
  },
): TradeFormSchema {
  // Filter from assets based on to token selection
  const filteredFromAssets = filterTradeAssets(fromAssets, paramToToken, currentFilterTab)

  // Filter to assets based on from token selection
  const filteredToAssets = filterTradeAssets(toAssets, paramFromToken, currentFilterTab)

  // Try to find tokens based on URL parameters first (only if they're compatible with current mode)
  const paramFromTokenAsset = paramFromToken
    ? filteredFromAssets.find(
        (asset) =>
          asset.token.symbol === paramFromToken &&
          (paramFromL1 === 'true'
            ? (asset as BridgeToken).tokenId !== undefined
            : (asset as BridgeToken).tokenId === undefined),
      )
    : null

  const paramToTokenAsset = paramToToken
    ? filteredToAssets.find(
        (asset) =>
          asset.token.symbol === paramToToken &&
          (paramToL1 === 'true'
            ? (asset as BridgeToken).tokenId !== undefined
            : (asset as BridgeToken).tokenId === undefined),
      )
    : null

  // If switching to limit mode, ignore URL parameters that aren't compatible
  const useUrlParams = currentFilterTab !== 'Limit' || (paramFromTokenAsset && paramToTokenAsset)

  // Fallback to default logic if URL parameters don't match available assets
  let defaultFromToken: (BridgeToken | TokenWithBalance) | null = null
  let defaultToToken: (BridgeToken | TokenWithBalance) | null = null

  if (useUrlParams) {
    defaultFromToken = paramFromTokenAsset ?? null
    defaultToToken = paramToTokenAsset ?? null
  }

  // Handle limit/TWAP mode defaults
  if (currentFilterTab === 'Limit' || currentFilterTab === 'TWAP') {
    // First, try to set USDC as the default "to" token
    if (!defaultToToken) {
      defaultToToken =
        filteredToAssets.find(
          (asset) => asset.token.symbol === 'USDC' && (asset as BridgeToken).tokenId !== undefined,
        ) ?? null
    }

    // Then find a non-USDC token for "from"
    if (!defaultFromToken) {
      defaultFromToken =
        filteredFromAssets.find(
          (asset) => (asset as BridgeToken).tokenId !== undefined && asset.token.symbol !== 'USDC',
        ) ?? null
    }

    // If we still don't have a from token, use any L1 asset that's not the same as to token
    if (!defaultFromToken) {
      defaultFromToken =
        filteredFromAssets.find(
          (asset) =>
            (asset as BridgeToken).tokenId !== undefined && asset.token.symbol !== defaultToToken?.token.symbol,
        ) ?? null
    }

    // If we still don't have a to token, use any L1 asset that's not the same as from token
    if (!defaultToToken) {
      defaultToToken =
        filteredToAssets.find(
          (asset) =>
            (asset as BridgeToken).tokenId !== undefined && asset.token.symbol !== defaultFromToken?.token.symbol,
        ) ?? null
    }
  } else {
    // Handle market mode defaults
    if (!defaultFromToken) {
      defaultFromToken = filteredFromAssets.find((asset) => (asset as BridgeToken).tokenId === undefined) ?? null
    }

    if (!defaultToToken) {
      defaultToToken =
        filteredToAssets.find(
          (asset) => asset.token.symbol === 'USDXL' && (asset as BridgeToken).tokenId === undefined,
        ) ??
        filteredToAssets.find(
          (asset) =>
            (asset as BridgeToken).tokenId === undefined && asset.token.symbol !== defaultFromToken?.token.symbol,
        ) ??
        null
    }
  }

  return {
    from: {
      symbol: defaultFromToken ? defaultFromToken.token.symbol : TokenSymbol('-'),
      value: '',
      isL1: defaultFromToken ? (defaultFromToken as BridgeToken).tokenId !== undefined : false,
    },
    to: {
      symbol: defaultToToken ? defaultToToken.token.symbol : TokenSymbol('-'),
      value: '',
      isL1: defaultToToken ? (defaultToToken as BridgeToken).tokenId !== undefined : false,
    },
    isMaxSelected: false,
    limitPrice: '',
    twapHours: 0,
    twapMinutes: 5,
    twapRandomize: false,
  }
}

interface UseTradeFormFieldsArgs {
  form: UseFormReturn<TradeFormSchema>
  assets: (BridgeToken | TokenWithBalance)[]
  fieldName: 'from' | 'to'
  counterpartFieldName: 'to' | 'from'
  tokensInfo: TokensInfo
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  l1Balances: SpotBalance[]
  exchangeRate: string
  toAssetsRef: React.MutableRefObject<(BridgeToken | TokenWithBalance)[]>
  marketInfo: MarketInfo
  lastEditedFieldRef: React.MutableRefObject<'from' | 'to' | null>
  currentTab?: string
  currentPage: TradeCurrentPage
}

export interface UseTradeFormFieldsResults {
  selectedAsset: BridgeToken | TokenWithBalance
  availableAssets: (BridgeToken | TokenWithBalance)[]
  changeToken: (newSymbol: TokenSymbol, isL1: boolean) => void
  maxSelectedFieldName: string | undefined
  maxValue: NormalizedUnitNumber
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSetMax: ((max: NormalizedUnitNumber) => void) | undefined
}

export function swapTokens(form: UseFormReturn<TradeFormSchema>, currentTab?: string): void {
  const currentFromSymbol = form.getValues('from.symbol')
  const currentFromValue = form.getValues('from.value')
  const currentFromIsL1 = form.getValues('from.isL1')

  const currentToSymbol = form.getValues('to.symbol')
  const currentToIsL1 = form.getValues('to.isL1')

  form.setValue(
    'from',
    { symbol: currentToSymbol, value: currentFromValue, isL1: currentToIsL1 },
    { shouldValidate: true },
  )

  form.setValue('to', { symbol: currentFromSymbol, value: '', isL1: currentFromIsL1 }, { shouldValidate: true })

  form.setValue('isMaxSelected', false, { shouldValidate: false })

  // Clear limit price when swapping in limit mode
  if (currentTab === 'Limit') {
    form.setValue('limitPrice', '', { shouldValidate: false })
  }
}

export function useTradeFormFields({
  form,
  assets,
  fieldName,
  counterpartFieldName,
  onChange,
  exchangeRate,
  toAssetsRef,
  marketInfo,
  lastEditedFieldRef,
  currentTab,
  currentPage,
}: UseTradeFormFieldsArgs): UseTradeFormFieldsResults {
  const router = useRouter()
  const formValues = form.getValues()
  const currentSymbol = formValues[fieldName].symbol
  const currentValue = formValues[fieldName].value
  const currentIsL1 = formValues[fieldName].isL1

  const currentCounterpartSymbol = formValues[counterpartFieldName].symbol

  const filteredAssets = filterTradeAssets(assets, currentCounterpartSymbol, currentTab)

  const selectedAsset = filteredAssets.find(
    (asset) =>
      asset.token.symbol === currentSymbol &&
      (currentIsL1 ? (asset as BridgeToken).tokenId !== undefined : (asset as BridgeToken).tokenId === undefined),
  )

  function changeToken(newSymbol: TokenSymbol, isL1: boolean): void {
    // If the selected token is the same as the counterpart token, swap them
    // This is how Uniswap handles it
    form.setValue('isMaxSelected', false, { shouldValidate: false })

    const counterpartSymbol = formValues[counterpartFieldName].symbol
    const counterpartIsL1 = formValues[counterpartFieldName].isL1

    if (newSymbol === counterpartSymbol && isL1 === counterpartIsL1) {
      if (currentPage !== 'modal') {
        router.replace(
          `/${currentPage}?from=${form.getValues('to.symbol')}&to=${form.getValues('from.symbol')}&fromL1=${form.getValues('to.isL1')}&toL1=${form.getValues('from.isL1')}`,
          {
            scroll: false,
          },
        )
      }

      swapTokens(form, currentTab)
      return
    }

    if (currentPage !== 'modal') {
      router.replace(
        `/${currentPage}?${fieldName}=${newSymbol}&${counterpartFieldName}=${counterpartSymbol}&${fieldName}L1=${isL1}&${counterpartFieldName}L1=${counterpartIsL1}`,
        {
          scroll: false,
        },
      )
    }

    form.setValue(fieldName, { symbol: newSymbol, value: currentValue, isL1 }, { shouldValidate: false })

    // Clear field when asset changes to avoid showing stale calculations
    if (lastEditedFieldRef.current) {
      form.setValue(`${lastEditedFieldRef.current === 'from' ? 'to' : 'from'}.value`, '', { shouldValidate: false })
    }

    // Clear limit price when changing tokens in limit mode
    if (currentTab === 'Limit') {
      form.setValue('limitPrice', '', { shouldValidate: false })
    }

    form.trigger()
  }

  const maxValue =
    fieldName === 'from'
      ? getTradeMaxValue({
          balance: selectedAsset?.balance || NormalizedUnitNumber(0),
          isNativeAsset: marketInfo.nativeAssetInfo.nativeAssetSymbol === selectedAsset?.token.symbol,
          minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
        })
      : NormalizedUnitNumber(0)

  // Check if max button should be available (has balance and is from field)
  const isMaxButtonAvailable = fieldName === 'from' && maxValue.gt(0)

  return {
    selectedAsset: selectedAsset || {
      balance: NormalizedUnitNumber(0),
      token: new Token({
        symbol: TokenSymbol('-'),
        decimals: 6,
        address: CheckedAddress('0x0000000000000000000000000000000000000000'),
        unitPriceUsd: '0',
        name: '',
        isAToken: false,
      }),
    },
    availableAssets: filteredAssets,
    onChange,
    changeToken,
    maxSelectedFieldName: fieldName === 'from' ? 'isMaxSelected' : undefined,
    maxValue,
    onSetMax: isMaxButtonAvailable
      ? (max: NormalizedUnitNumber) => {
          lastEditedFieldRef.current = fieldName

          if (!selectedAsset) {
            form.setValue('from.value', '')
            return
          }

          // Set the from value first
          form.setValue('from.value', formFormat(max, selectedAsset.token.decimals, currentIsL1))

          // Calculate and set the to amount with current form values for validation
          const currentFormValues = {
            from: { symbol: form.getValues('from.symbol'), isL1: form.getValues('from.isL1') },
            to: { symbol: form.getValues('to.symbol'), isL1: form.getValues('to.isL1') },
          }

          calculateToAmount(form, exchangeRate, toAssetsRef, max, currentFormValues)

          // Trigger validation after both values are set
          void setTimeout(() => {
            form.trigger()
          }, 0)
        }
      : undefined,
  }
}

function getTradeMaxValue({
  balance,
  isNativeAsset,
  minRemainingNativeAsset,
}: {
  balance: NormalizedUnitNumber
  isNativeAsset: boolean
  minRemainingNativeAsset: NormalizedUnitNumber
}): NormalizedUnitNumber {
  const balanceBasedMaxDeposit = balance.minus(isNativeAsset ? minRemainingNativeAsset : NormalizedUnitNumber(0))

  return NormalizedUnitNumber(BigNumber.max(balanceBasedMaxDeposit, 0))
}

export interface TradeFormNormalizedData {
  from: BridgeToken | TokenWithBalanceAndFormValue
  to: BridgeToken | TokenWithBalanceAndFormValue
}

export function normalizeFormValues(
  values: TradeFormSchema,
  fromAssets: (BridgeToken | TokenWithBalance)[],
  toAssets: (BridgeToken | TokenWithBalance)[],
): TradeFormNormalizedData {
  function normalizeAsset(
    assetInput: { symbol: string; value: string; isL1: boolean },
    assets: (BridgeToken | TokenWithBalance)[],
  ): BridgeToken | TokenWithBalanceAndFormValue {
    const tradeToken = assets.find(
      (asset) =>
        asset.token.symbol === assetInput.symbol &&
        (assetInput.isL1 ? (asset as BridgeToken).tokenId !== undefined : (asset as BridgeToken).tokenId === undefined),
    )

    if (!tradeToken) {
      raise('No assets available')
    }

    return {
      ...tradeToken,
      value: NormalizedUnitNumber(assetInput.value === '' ? '0' : assetInput.value),
    }
  }

  return {
    from: normalizeAsset(values.from, fromAssets),
    to: normalizeAsset(values.to, toAssets),
  }
}

export function useCreateObjectives(
  formValues: TradeFormNormalizedData,
  fromAssets: (BridgeToken | TokenWithBalance)[],
  toAssets: (BridgeToken | TokenWithBalance)[],
): TradeObjective[] {
  const fromToken =
    fromAssets.find(
      (asset) =>
        asset.token.symbol === formValues.from.token.symbol &&
        ((asset as BridgeToken).tokenId !== undefined) === ((formValues.from as BridgeToken).tokenId !== undefined),
    ) ?? raise('No from assets available')

  const toToken =
    toAssets.find(
      (asset) =>
        asset.token.symbol === formValues.to.token.symbol &&
        ((asset as BridgeToken).tokenId !== undefined) === ((formValues.to as BridgeToken).tokenId !== undefined),
    ) ?? raise('No to assets available')

  return [
    {
      from: fromToken,
      to: toToken,
      amount: formValues.from.value ?? NormalizedUnitNumber(0),
    },
  ]
}

export function calculateToAmount(
  form: UseFormReturn<TradeFormSchema>,
  exchangeRate: string,
  toAssetsRef: React.MutableRefObject<(BridgeToken | TokenWithBalance)[]>,
  max: NormalizedUnitNumber,
  currentFormValues?: { from: { symbol: string; isL1: boolean }; to: { symbol: string; isL1: boolean } },
): void {
  const toSymbol = form.getValues('to.symbol')
  const toIsL1 = form.getValues('to.isL1')
  const fromSymbol = form.getValues('from.symbol')
  const fromIsL1 = form.getValues('from.isL1')

  // Validate that the exchange rate belongs to the current token pair
  if (currentFormValues) {
    const currentFromSymbol = currentFormValues.from.symbol
    const currentToSymbol = currentFormValues.to.symbol
    const currentFromIsL1 = currentFormValues.from.isL1
    const currentToIsL1 = currentFormValues.to.isL1

    // Check if the form values have changed since the exchange rate was calculated
    if (
      fromSymbol !== currentFromSymbol ||
      toSymbol !== currentToSymbol ||
      fromIsL1 !== currentFromIsL1 ||
      toIsL1 !== currentToIsL1
    ) {
      // Exchange rate is stale, don't use it for calculations
      console.warn('Exchange rate is stale for current token pair in calculateToAmount, skipping calculation')
      form.setValue('to.value', '', { shouldValidate: true })
      return
    }
  }

  const toAsset = toAssetsRef.current.find(
    (asset) => asset.token.symbol === toSymbol && ((asset as BridgeToken).tokenId !== undefined) === toIsL1,
  )

  if (exchangeRate && Number(exchangeRate) > 0 && exchangeRate !== 'N/A') {
    const fromAmount = Number(max)
    const rate = Number(exchangeRate)

    const toAmount = fromAmount * rate

    if (toAsset) {
      const formattedAmount = formFormat(NormalizedUnitNumber(toAmount), toAsset.token.decimals, toIsL1)

      form.setValue('to.value', formattedAmount, { shouldValidate: true })
    }
  } else {
    // If no valid exchange rate, clear the to field
    form.setValue('to.value', '', { shouldValidate: true })
  }
}
